"use client";

import { ThemeProvider as NextThemeProvider } from "next-themes";
import { memo, useEffect } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";

import { appConfig } from "~/config/app";

import type { ThemeConfig } from "@workspace/ui";

export const useThemeConfig = create<{
  config: Omit<ThemeConfig, "mode">;
  setConfig: (config: Omit<ThemeConfig, "mode">) => void;
}>()(
  persist(
    (set) => ({
      config: appConfig.theme.default,
      setConfig: (config) => set({ config }),
    }),
    {
      name: "theme-config",
    },
  ),
);

interface ThemeProviderProps {
  readonly children: React.ReactNode;
}

export const ThemeProvider = memo<ThemeProviderProps>(({ children }) => {
  const config = useThemeConfig((state) => state.config);

  useEffect(() => {
    const defaultColor = appConfig.theme.default.color;

    const applyThemeColor = (color: string | null | undefined) => {
      document.documentElement.dataset.theme = color ?? defaultColor;
    };

    applyThemeColor(config.color);

    const restoreTheme = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      try {
        const raw = localStorage.getItem("theme-config");
        const parsed = raw
          ? (JSON.parse(raw) as {
              state?: { config?: { color?: string } };
            })
          : null;
        applyThemeColor(parsed?.state?.config?.color ?? null);
      } catch {
        applyThemeColor(null);
      }
    };

    document.addEventListener("visibilitychange", restoreTheme);
    window.addEventListener("pageshow", restoreTheme);

    return () => {
      document.removeEventListener("visibilitychange", restoreTheme);
      window.removeEventListener("pageshow", restoreTheme);
    };
  }, [config.color]);

  return (
    <NextThemeProvider
      attribute="class"
      defaultTheme={appConfig.theme.default.mode}
      enableSystem
      disableTransitionOnChange
      scriptProps={{ type: "application/json" }}
    >
      {children}
    </NextThemeProvider>
  );
});

ThemeProvider.displayName = "ThemeProvider";
